import { Alert, Box, Container, Spinner } from "@sembark-travel/ui-base"
import { Breadcrumbs } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { Helmet } from "react-helmet-async"
import useSWR from "swr"
import { generatePath } from "../router-utils"
import { VisaServiceForm } from "./Item"
import {
	TVisa,
	TVisaServiceForm,
	XHR,
	getTypeOfVisaOption,
	getEntryTypeOption,
	getPurposeOfVisaOption,
	getProcessingTimeInHours,
} from "./store"
import { formatMoneyByDecimal } from "@sembark-travel/money"
import { useState } from "react"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

export function EditVisaService({
	visaId,
	onSuccess,
	onCancel,
}: Readonly<{
	visaId: string
	onSuccess: (visa: TVisa) => void
	onCancel?: () => void
}>) {
	const xhr = useXHR()
	const { data: service, error } = useSWR<TVisa>(
		`/api/visa-services/${visaId}`,
		() =>
			XHR(xhr)
				.showVisaServices(visaId)
				.then((res) => {
					return {
						...res,
						visa_types: res.visa_types.filter((vt) => !vt.deleted_at),
					}
				})
	)

	if (!service) {
		if (!error) {
			return <Spinner alignCenter padding="8" />
		}
		return (
			<Alert status="error">
				{error.message || "Sorry, the requested visa service was not found."}
			</Alert>
		)
	}

	return (
		<>
			<Helmet>
				<title>Edit Visa</title>
			</Helmet>
			<Breadcrumbs
				title="Edit Visa"
				items={[
					[generatePath("/visa"), "Visa"],
					[
						generatePath("/visa/:visaServiceId", {
							visaServiceId: String(visaId),
						}),
						service.name ? service.name : "",
					],
					["", "Edit"],
				]}
			/>
			<Box paddingY={{ md: "8" }}>
				<Box maxWidth="6xl" marginX="auto">
					<Box rounded="lg" bgColor="default">
						<Container paddingY="4" paddingX="0">
							<EditVisaServiceForm
								onSubmit={async (values: TVisaServiceForm) => {
									const response = await XHR(xhr).updateVisaService(
										visaId,
										values
									)
									showSnackbar(
										`The visa service has been updated successfully!`
									)
									onSuccess(response.data.data)
								}}
								onCancel={onCancel}
								service={service}
							/>
						</Container>
					</Box>
				</Box>
			</Box>
		</>
	)
}

export function EditVisaServiceForm({
	onSubmit,
	onCancel,
	service,
}: Readonly<{
	onSubmit: (data: TVisaServiceForm) => Promise<void>
	onCancel?: () => void
	service: TVisa
}>) {
	const [initialValues] = useState<TVisaServiceForm>(() => ({
		id: service.id,
		name: service.name,
		currency: service.currency,
		countries: service.countries,
		destinations: service.trip_destinations,
		document_groups: service.document_groups
			? service.document_groups[0]
			: undefined,
		visa_types: service.visa_types
			.filter((vt) => !vt.deleted_at)
			.map((vt) => ({
				id: vt.id,
				name: vt.name,
				type_of_visa: getTypeOfVisaOption(vt.type_of_visa),
				entry_type: getEntryTypeOption(vt.entry_type),
				purpose: getPurposeOfVisaOption(vt.purpose),
				day_and_hour: getProcessingTimeInHours(vt.processing_time_in_hours),
				duration: vt.duration_iso,
				cost_price:
					vt.cost_price && typeof vt.cost_price === "object"
						? formatMoneyByDecimal(vt.cost_price)
						: undefined,
				selling_price:
					vt.selling_price && typeof vt.selling_price === "object"
						? formatMoneyByDecimal(vt.selling_price)
						: undefined,
				price_valid_till: vt.price_valid_till,
				apply_before_days: vt.apply_before_days,
				document_groups: Array.isArray(vt.document_groups)
					? vt.document_groups[0]
					: undefined,
				description: vt.description,
			})),
	}))
	return (
		<VisaServiceForm
			onSubmit={onSubmit}
			onCancel={onCancel}
			initialValues={initialValues}
		/>
	)
}
